import React, { useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { PageHeader, ActionBar } from "@/components/PageBits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const empty = { email: "", name: "", password: "", role: "innendienst", is_subcontractor: false, telekom_konto: "" };

export default function UsersAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => { const { data } = await api.get("/users"); setItems(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditingId(null); setForm(empty); setOpen(true); };
  const startEdit = (u) => { setEditingId(u.id); setForm({ ...empty, ...u, password: "" }); setOpen(true); };

  const save = async () => {
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.put(`/users/${editingId}`, payload);
      } else {
        if (!form.password) { toast.error("Passwort erforderlich"); return; }
        await api.post("/auth/register", form);
      }
      toast.success("Gespeichert"); setOpen(false); await load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Benutzer ${u.email} löschen?`)) return;
    try { await api.delete(`/users/${u.id}`); load(); toast.success("Gelöscht"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="users-admin">
      <PageHeader
        title="Benutzerverwaltung"
        subtitle="Mitarbeiter und Admin-Accounts verwalten"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="h-12 px-5 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={startNew} data-testid="user-new-btn">
                <Plus className="h-4 w-4 mr-2" /> Neuer Benutzer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editingId ? "Benutzer bearbeiten" : "Neuer Benutzer"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name *</Label><Input className="h-11 mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="u-name" /></div>
                <div className="col-span-2"><Label>E-Mail *</Label><Input className="h-11 mt-1" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} data-testid="u-email" /></div>
                <div className="col-span-2"><Label>{editingId ? "Neues Passwort (optional)" : "Passwort *"}</Label><Input className="h-11 mt-1" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="u-password" /></div>
                <div><Label>Rolle</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="u-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="innendienst">Innendienst</SelectItem>
                      <SelectItem value="buchhaltung">Buchhaltung</SelectItem>
                      <SelectItem value="disponent">Disponent</SelectItem>
                      <SelectItem value="techniker">Techniker</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Telekom Konto</Label><Input className="h-11 mt-1" value={form.telekom_konto || ""} onChange={(e) => setForm({ ...form, telekom_konto: e.target.value })} /></div>
                <div className="col-span-2 flex items-center justify-between bg-slate-50 p-3 rounded">
                  <Label>Subunternehmer</Label>
                  <Switch checked={form.is_subcontractor} onCheckedChange={(v) => setForm({ ...form, is_subcontractor: v })} />
                </div>
              </div>
              <DialogFooter><Button className="h-11 bg-[#0A2540] hover:bg-[#113A63] text-white" onClick={save} data-testid="u-save">Speichern</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "#E2E8F0" }}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">E-Mail</th>
              <th className="text-left px-4 py-3">Rolle</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="w-32"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t" style={{ borderColor: "#E2E8F0" }}>
                <td className="px-4 py-2.5 font-semibold">{u.name}</td>
                <td className="px-4 py-2.5">{u.email}</td>
                <td className="px-4 py-2.5"><span className="text-xs font-semibold uppercase">{u.role}</span></td>
                <td className="px-4 py-2.5"><span className={`text-xs font-semibold ${u.is_active ? "text-emerald-700" : "text-slate-400"}`}>{u.is_active ? "Aktiv" : "Inaktiv"}</span></td>
                <td className="px-4 py-2.5 flex justify-end gap-2">
                  <button className="p-1.5 hover:text-[#0A2540]" onClick={() => startEdit(u)} data-testid={`u-edit-${u.email}`}><Pencil className="h-4 w-4" /></button>
                  <button className="p-1.5 hover:text-red-600" onClick={() => del(u)} data-testid={`u-del-${u.email}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ActionBar onPrint={() => window.print()} hideNext />
    </div>
  );
}
